import React, { useEffect } from "react";
import {
  FaCloud,
  FaAndroid,
  FaCogs,
  FaLaptopCode,
  FaCode,
  FaBug,
  FaBrain,
  FaLayerGroup,
  FaInfinity,
  FaChartLine,
  FaSalesforce,
  FaEnvelope,
  FaShieldAlt,
  FaArrowRight,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./Services.css";
import AOS from "aos";
import "aos/dist/aos.css";

// 🔹 IMPORT IMAGES
import cloudImg from "../../assets/cloud.jpg";
import androidImg from "../../assets/android.jpg";
import mobileImg from "../../assets/mobile.jpg";
import saasImg from "../../assets/saas.jpg";
import uiuxImg from "../../assets/uiux.jpg";
import webImg from "../../assets/web.jpg";
import testingImg from "../../assets/testing.jpg";
import aiImg from "../../assets/ai.jpg";
import fullstackImg from "../../assets/fullstack.jpg";
import devopsImg from "../../assets/devops.jpg";
import dataImg from "../../assets/data.jpg";
import salesforceImg from "../../assets/salesforce.jpg";
import cyberImg from "../../assets/cyber.jpg";

const Services = () => {
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 1000, once: true, easing: "ease-out-cubic" });
  }, []);

  const servicesData = [
    {
      id: 1,
      icon: <FaCloud />,
      title: "Cloud Solutions",
      shortDesc:
        "Secure, scalable, and cost-efficient cloud solutions designed to modernize your IT infrastructure.",
      longDesc:
        "Our Cloud Solutions help businesses transition seamlessly to the cloud while ensuring performance, security, and scalability. We specialize in cloud consulting, migration, architecture design, optimization, and ongoing management across leading platforms such as AWS, Microsoft Azure, and Google Cloud.",
      image: cloudImg,
    },
    {
      id: 2,
      icon: <FaAndroid />,
      title: "Android Development",
      shortDesc:
        "High-performance Android applications built for scalability, security, and superior user experience.",
      longDesc:
        "We design and develop custom Android applications tailored to your business goals. Using Java and Kotlin, our team follows Google’s best practices and Material Design principles to deliver secure, fast, and user-friendly apps.",
      image: androidImg,
    },
    {
      id: 4,
      icon: <FaCogs />,
      title: "SaaS Development",
      shortDesc: "Scalable SaaS platforms engineered for performance, security, and business growth.",
      longDesc:
        "Our SaaS Development services focus on building cloud-based software solutions that are scalable, secure, and subscription-ready. We design multi-tenant architectures, integrate payment gateways, analytics dashboards, and role-based access controls.",
      image: saasImg,
    },
    {
      id: 5,
      icon: <FaLaptopCode />,
      title: "UI / UX Design",
      shortDesc: "User-centric designs that enhance engagement and drive business results.",
      longDesc:
        "We create intuitive, visually appealing, and conversion-focused UI/UX designs that align with your brand identity. Our process includes user research, wireframing, prototyping, usability testing, and responsive design.",
      image: uiuxImg,
    },
    {
      id: 6,
      icon: <FaCode />,
      title: "Web Development",
      shortDesc: "Modern, responsive, and SEO-friendly websites built for performance and scalability.",
      longDesc:
        "Our Web Development services deliver fast, secure, and scalable websites using modern frameworks and technologies. We develop custom websites, web applications, and APIs optimized for performance, SEO, and cross-browser compatibility.",
      image: webImg,
    },
    {
      id: 7,
      icon: <FaBug />,
      title: "Software Testing",
      shortDesc: "Comprehensive testing services ensuring quality, security, and performance.",
      longDesc:
        "We provide end-to-end software testing services to ensure your applications meet the highest quality standards. Our QA experts perform manual testing, automation testing, performance testing, security testing, and regression testing.",
      image: testingImg,
    },
    {
      id: 8,
      icon: <FaBrain />,
      title: "AI / ML Solutions",
      shortDesc: "Intelligent AI-powered solutions that drive automation and smarter decision-making.",
      longDesc:
        "Our AI and Machine Learning solutions help businesses leverage data to gain actionable insights and automate processes. We develop chatbots, recommendation systems, predictive analytics models, NLP solutions, and custom ML algorithms.",
      image: aiImg,
    },
    {
      id: 9,
      icon: <FaLayerGroup />,
      title: "Full Stack Development",
      shortDesc: "End-to-end application development from frontend to cloud deployment.",
      longDesc:
        "We offer full stack development services covering frontend, backend, database management, APIs, and cloud deployment. Our developers work with modern technologies to build scalable, secure, and high-performance applications.",
      image: fullstackImg,
    },
    {
      id: 10,
      icon: <FaInfinity />,
      title: "DevOps Services",
      shortDesc: "Automation-driven DevOps solutions for faster and reliable deployments.",
      longDesc:
        "Our DevOps services streamline development and operations through CI/CD pipelines, cloud automation, and monitoring. We implement Docker, Kubernetes, infrastructure as code, and continuous integration practices.",
      image: devopsImg,
    },
    {
      id: 11,
      icon: <FaChartLine />,
      title: "Data Analytics",
      shortDesc: "Transform raw data into meaningful insights for smarter business decisions.",
      longDesc:
        "We help organizations unlock the power of data through advanced analytics and visualization. Our services include data integration, dashboard development, BI tools, and real-time reporting.",
      image: dataImg,
    },
    {
      id: 12,
      icon: <FaSalesforce />,
      title: "Salesforce Development",
      shortDesc: "Customized Salesforce CRM solutions to optimize customer relationships.",
      longDesc:
        "Our Salesforce development services deliver tailored CRM solutions using Sales Cloud, Service Cloud, Apex, Lightning Components, and integrations. We help businesses automate workflows, enhance customer engagement, and gain real-time insights.",
      image: salesforceImg,
    },
    {
      id: 13,
      icon: <FaShieldAlt />,
      title: "Cyber Security",
      shortDesc: "Advanced cyber security solutions to protect systems, networks, and data.",
      longDesc:
        "Our Cyber Security services safeguard your digital assets against evolving threats. We offer vulnerability assessments, penetration testing, application and network security, cloud security, identity and access management, and compliance support.",
      image: cyberImg,
    },
  ];

  const handleReadMore = (service) => {
    navigate(`/services/${service.id}`, {
      state: {
        title: service.title,
        longDesc: service.longDesc,
        image: service.image,
      },
    });
  };

  return (
    <section className="services-section" id="services">
      <h2 className="section-title">Our Expertise</h2>

      <div className="services-grid">
        {servicesData.map((service, index) => (
          <div
            key={service.id}
            className="service-card"
            style={{ "--bg-image": `url(${service.image})` }}
            data-aos="fade-up"
            data-aos-delay={index * 50}
          >
            {/* CONTACT BUTTON WRAPPER */}
            <div
              className="contact-btn-wrapper"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/contact");
              }}
              title="Contact Us"
            >
              <div className="contact-icon-btn">
                <FaEnvelope />
              </div>
            </div>

            {/* ICON WITH DIAMOND BACKGROUND */}
            <div className="service-icon-wrapper">
              <div className="service-icon-bg"></div>
              <div className="service-icon">{service.icon}</div>
            </div>

            {/* CONTENT */}
            <span className="service-number">
              Service {String(index + 1).padStart(2, "0")}
            </span>
            <h3>{service.title}</h3>
            <p>{service.shortDesc}</p>

            {/* ACTION BUTTON */}
            <button
              className="view-details-btn"
              onClick={() => handleReadMore(service)}
            >
              View Details <FaArrowRight size={14} />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Services;